import { create } from '@bufbuild/protobuf';
import useCloudService from '@grpc/composables/useCloudService';
import useCloudServiceV2 from '@grpc/composables/useCloudServiceV2';
import { useSessionStore } from '@pinia/session';
import {
  type Project,
  type ProjectCloudProvider,
  type ProjectInvite,
  type ProjectLog,
  ProjectMemberRole,
  type ProjectSSHKey,
  type ProjectUsage,
} from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import {
  ChangeDefaultProjectRequestSchema,
  CreateProjectCloudProviderRequestSchema,
  type CreateProjectRequest,
  DeleteProjectCloudProviderRequestSchema,
  DeleteProjectRequestSchema,
  GetProjectUsageRequestSchema,
  ImportProjectCloudProviderResourceRequestSchema,
  InviteMemberToProjectRequestSchema,
  JoinProjectRequestSchema,
  LeaveProjectRequestSchema,
  type ListProjectCloudProviderResourcesRequest,
  ListProjectCloudProvidersRequestSchema,
  ListProjectSSHKeysRequestSchema,
  ListProjectsRequestSchema,
  RemoveMemberFromProjectRequestSchema,
  UpdateProjectCloudProviderRequestSchema,
  UpdateProjectRequestSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import type {
  ChangeDefaultProjectResponse,
  CreateProjectCloudProviderResponse,
  CreateProjectResponse,
  DeleteProjectCloudProviderResponse,
  DeleteProjectResponse,
  ImportProjectCloudProviderResourceResponse,
  InviteMemberToProjectResponse,
  JoinProjectResponse,
  LeaveProjectResponse,
  ListProjectCloudProviderResourcesResponse,
  ListProjectCloudProvidersResponse,
  ListProjectSSHKeysResponse,
  ListProjectsResponse,
  RemoveMemberFromProjectResponse,
  UpdateProjectCloudProviderResponse,
  UpdateProjectResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import { ListProjectLogsRequestSchema } from 'gpcore-grpc/gpcore/api/cloud/v2/requests_pb';
import type { ListProjectLogsResponse } from 'gpcore-grpc/gpcore/api/cloud/v2/responses_pb';
import type { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import { PaginationRequestSchema } from 'gpcore-grpc/gpcore/type/v1/pagination_pb';
import { defineStore } from 'pinia';
import { computed, reactive, toRefs } from 'vue';

export interface AuditLogRequest {
  page: number;
  userId: string;
  search: string;
}

export interface ProjectCloudProvidersRequest {
  page: number;
}

export interface JoinProject {
  projectId: string;
  accept: boolean;
}

export interface ProjectStoreState {
  projects: Array<Project>;
  invites: Array<ProjectInvite>;
  auditLog: Array<ProjectLog>;
  sshKeys: Array<ProjectSSHKey>;
  cloudProviders: Array<ProjectCloudProvider>;
  currentUsage?: ProjectUsage;
  current: string;
}

export const useProjectStore = defineStore(
  'project',
  () => {
    const cloudService = useCloudService();
    const cloudServiceV2 = useCloudServiceV2();
    const sessionStore = useSessionStore();

    const state = reactive<ProjectStoreState>({
      projects: new Array<Project>(),
      invites: new Array<ProjectInvite>(),
      auditLog: new Array<ProjectLog>(),
      sshKeys: new Array<ProjectSSHKey>(),
      cloudProviders: new Array<ProjectCloudProvider>(),
      current: '',
      currentUsage: undefined,
    });

    const projectId = computed((): string => state.current);

    const projectIds = computed(() => (state.projects as Array<Project>).map((p: Project) => p.id));

    const projectUsage = computed(() => {
      if (state.currentUsage === undefined) {
        return undefined;
      }

      return state.currentUsage;
    });

    const currentUserMembership = computed(() => {
      const { session } = sessionStore;
      const project = state.projects.find((s) => s.id === state.current);
      if (project === undefined) {
        return undefined;
      }

      for (const member of project.members) {
        if (member.user!.id === session.user.id) {
          return member;
        }
      }

      return undefined;
    });

    const project = computed(() => {
      if (state.current === '') {
        if (state.projects.length === 1) return state.projects[0];
        return undefined;
      }

      return state.projects.find((s) => s.id === state.current);
    });

    const projectOwner = computed(() => {
      if (project.value == null) {
        return undefined;
      }

      return project.value.members.find((m) => m.role === ProjectMemberRole.OWNER);
    });

    /**
     * Not to be confused with `userStore.nodeLimit`.
     *
     * This is the node limit for the project owner.
     */
    const projectNodeLimit = computed(() => {
      if (projectOwner.value == null) {
        return undefined;
      }

      return projectOwner.value.user.nodeLimit;
    });

    // TODO: why so complicated? Does it make sense to move the default project id to user instead?
    const defaultProjectId = computed(() => {
      const { session } = sessionStore;

      const defaultProject = state.projects.find((p) => {
        const memberShip = p.members.find((m) => {
          if (m.user.id === session.user.id && m.default) {
            return m;
          }

          return undefined;
        });

        if (memberShip !== undefined) {
          return p;
        }

        return undefined;
      });

      if (defaultProject != null) {
        return defaultProject.id;
      }

      return undefined;
    });

    const hasBillingProfile = computed(
      () =>
        project.value != null &&
        project.value.billingProfile != null &&
        project.value.billingProfile.id !== '',
    );

    // migrated mutations
    const setProject = (project: Project) => {
      const existingIndex = state.projects.findIndex((s) => s.id === project.id);
      if (existingIndex > -1) {
        state.projects[existingIndex] = project;
      } else {
        state.projects.push(project);
      }
    };

    const changeProjectState = (id: string) => {
      state.current = id;
      state.auditLog = new Array<ProjectLog>();
    };

    const deleteProjectState = (project: Project) => {
      const existingIndex = state.projects.findIndex((s) => s.id === project.id);
      if (existingIndex > -1) {
        if (state.current === project.id) {
          state.current = '';
        }

        state.projects.splice(existingIndex, 1);
      }
    };

    // migrated actions
    const createProject = async (request: CreateProjectRequest): Promise<CreateProjectResponse> => {
      const { response, error } = await cloudService.executeApiCall('createProject', request);

      if (error != null) throw error;

      setProject(response.project);

      return response;
    };

    const updateProject = async (project: Project): Promise<UpdateProjectResponse> => {
      const request = create(UpdateProjectRequestSchema);
      request.id = project.id;
      request.name = project.name;
      if (project.billingProfile !== undefined) {
        request.billingAddressId = project.billingProfile.id;
      }

      const { response, error } = await cloudService.executeApiCall('updateProject', request);
      if (error != null) throw error;

      setProject(response.project);

      return response;
    };

    const deleteProject = async (project: Project): Promise<DeleteProjectResponse> => {
      const request = create(DeleteProjectRequestSchema);
      request.id = project.id;

      const { response, error } = await cloudService.executeApiCall('deleteProject', request);
      if (error != null) throw error;

      deleteProjectState(project);

      if (project.id === state.current) {
        setProject(undefined);
      }

      return response;
    };

    const getUsage = async (projectId: string): Promise<ProjectUsage> => {
      const request = create(GetProjectUsageRequestSchema);
      request.id = projectId;
      request.detailed = false;

      const { response, error } = await cloudService.executeApiCall('getProjectUsage', request);
      if (error != null) throw error;

      return response.usage;
    };

    const reloadUsageForCurrentProject = async (): Promise<void> => {
      state.currentUsage = undefined;

      if (state.current !== '') {
        state.currentUsage = await getUsage(state.current);
      }
    };

    const changeProject = (id: string): void => {
      changeProjectState(id);
      reloadUsageForCurrentProject();
    };

    const reloadProjects = async (page = 1): Promise<ListProjectsResponse> => {
      const request = create(ListProjectsRequestSchema);
      const pagination = create(PaginationRequestSchema);
      pagination.page = page;
      request.pagination = pagination;

      const { response, error } = await cloudService.executeApiCall('listProjects', request);
      if (error != null) throw error;
      state.projects = response.projects;
      state.invites = response.invites;
      if ((state.current == null || state.current.length === 0) && defaultProjectId.value) {
        try {
          await changeProject(defaultProjectId.value);
        } catch (error) {
          // do nothing
        }
      }

      return response;
    };

    const reloadAuditLog = async (req: AuditLogRequest): Promise<ListProjectLogsResponse> => {
      const request = create(ListProjectLogsRequestSchema);
      const pagination = create(PaginationRequestSchema);
      pagination.page = req.page;
      request.pagination = pagination;
      request.id = project.value.id;
      request.userId = req.userId;
      request.search = req.search;

      const { response, error } = await cloudServiceV2.executeApiCall('listProjectLogs', request);
      if (error != null) throw error;

      state.auditLog = response.logs;

      return response;
    };

    const inviteMember = async (identifier: string): Promise<InviteMemberToProjectResponse> => {
      const request = create(InviteMemberToProjectRequestSchema);
      request.id = project.value.id;
      request.identifier = identifier;

      const { response, error } = await cloudService.executeApiCall(
        'inviteMemberToProject',
        request,
      );

      if (error != null) throw error;

      return response;
    };

    const removeMember = async (id: string): Promise<RemoveMemberFromProjectResponse> => {
      const request = create(RemoveMemberFromProjectRequestSchema);
      request.id = project.value.id;
      request.userId = id;

      const { response, error } = await cloudService.executeApiCall(
        'removeMemberFromProject',
        request,
      );
      if (error != null) throw error;

      const { members } = project.value;
      const existingIndex = members.findIndex((s) => s.user?.id === id);
      if (existingIndex > -1) {
        members.splice(existingIndex, 1);
      }

      project.value.members = members;
      // Maybe todo
      setProject(project.value as Project);

      return response;
    };

    const reloadProjectSSHKeys = async (): Promise<ListProjectSSHKeysResponse> => {
      const request = create(ListProjectSSHKeysRequestSchema);
      request.id = project.value.id;

      const { response, error } = await cloudService.executeApiCall('listProjectSSHKeys', request);
      if (error != null) throw error;

      state.sshKeys = response.keys;

      return response;
    };

    const reloadProjectCloudProviders = async (
      req: ProjectCloudProvidersRequest,
    ): Promise<ListProjectCloudProvidersResponse> => {
      const request = create(ListProjectCloudProvidersRequestSchema);
      const pagination = create(PaginationRequestSchema);
      pagination.page = req.page;
      request.pagination = pagination;
      request.projectId = project.value.id;

      const { response, error } = await cloudService.executeApiCall(
        'listProjectCloudProviders',
        request,
      );
      if (error != null) throw error;
      state.cloudProviders = response.cloudProviders;

      return response;
    };

    const createProjectCloudProvider = async ({
      name,
      type,
      region,
      apiKeyId,
      apiKeySecret,
    }: {
      name: string;
      type: CloudProviderType;
      region: string;
      apiKeyId: string;
      apiKeySecret: string;
    }): Promise<CreateProjectCloudProviderResponse> => {
      const request = create(CreateProjectCloudProviderRequestSchema, {
        projectId: project.value.id,
        name,
        type,
        region,
        apiKeyId,
        apiKeySecret,
      });

      const { response, error } = await cloudService.executeApiCall(
        'createProjectCloudProvider',
        request,
      );
      if (error != null) throw error;

      await reloadProjectCloudProviders({ page: 1 });

      return response;
    };

    const updateProjectCloudProvider = async ({
      id,
      name,
      region,
      apiKeyId,
      apiKeySecret,
    }: {
      id: string;
      name: string;
      region: string;
      apiKeyId: string;
      apiKeySecret: string;
    }): Promise<UpdateProjectCloudProviderResponse> => {
      const request = create(UpdateProjectCloudProviderRequestSchema, {
        projectId: project.value.id,
        cloudProviderId: id,
        name,
        region,
        apiKeyId,
        apiKeySecret,
      });

      const { response, error } = await cloudService.executeApiCall(
        'updateProjectCloudProvider',
        request,
      );
      if (error != null) throw error;

      await reloadProjectCloudProviders({ page: 1 });

      return response;
    };

    const deleteProjectCloudProvider = async (
      id: string,
    ): Promise<DeleteProjectCloudProviderResponse> => {
      const cloudService = useCloudService();

      const request = create(DeleteProjectCloudProviderRequestSchema);
      request.cloudProviderId = id;
      request.projectId = project.value.id;

      const { response, error } = await cloudService.executeApiCall(
        'deleteProjectCloudProvider',
        request,
      );
      if (error != null) throw error;

      return response;
    };

    const listProjectCloudProviderResources = async (
      request: ListProjectCloudProviderResourcesRequest,
    ): Promise<ListProjectCloudProviderResourcesResponse> => {
      const { response, error } = await cloudService.executeApiCall(
        'listProjectCloudProviderResources',
        request,
      );
      if (error != null) throw error;

      return response;
    };

    const importProjectCloudProviderResource = async (
      projectId: string,
      cloudProviderId: string,
      resourceId: string,
    ): Promise<ImportProjectCloudProviderResourceResponse> => {
      const request = create(ImportProjectCloudProviderResourceRequestSchema);
      request.projectId = projectId;
      request.cloudProviderId = cloudProviderId;
      request.resourceId = resourceId;

      const { response, error } = await cloudService.executeApiCall(
        'importProjectCloudProviderResource',
        request,
      );
      if (error != null) throw error;

      return response;
    };

    const joinProject = async (join: JoinProject): Promise<JoinProjectResponse> => {
      const request = create(JoinProjectRequestSchema);
      request.id = join.projectId;
      request.accept = join.accept;

      const { response, error } = await cloudService.executeApiCall('joinProject', request);
      if (error != null) throw error;

      await reloadProjects();
      if (state.projects.length > 0 && (state.current == null || state.current.length === 0)) {
        changeProjectState(join.projectId);
      }
      // TODO: joinProject need subscribeProjects composable afterwards

      return response;
    };

    const setAsDefaultProject = async (id: string): Promise<ChangeDefaultProjectResponse> => {
      const request = create(ChangeDefaultProjectRequestSchema);
      request.id = id;

      const { response, error } = await cloudService.executeApiCall(
        'changeDefaultProject',
        request,
      );
      if (error != null) throw error;

      await reloadProjects();

      return response;
    };

    const leaveProject = async (id: string): Promise<LeaveProjectResponse> => {
      const request = create(LeaveProjectRequestSchema);
      request.id = id;

      const { response, error } = await cloudService.executeApiCall('leaveProject', request);
      if (error != null) throw error;

      await reloadProjects();

      return response;
    };

    return {
      ...toRefs(state as ProjectStoreState),
      project,
      projectId,
      projectIds,
      projectUsage,
      projectOwner,
      projectNodeLimit,
      currentUserMembership,
      defaultProjectId,
      hasBillingProfile,
      setProject,
      changeProjectState,
      reloadUsageForCurrentProject,
      deleteProjectState,
      createProject,
      updateProject,
      deleteProject,
      changeProject,
      reloadProjects,
      reloadAuditLog,
      inviteMember,
      removeMember,
      reloadProjectSSHKeys,
      reloadProjectCloudProviders,
      createProjectCloudProvider,
      updateProjectCloudProvider,
      deleteProjectCloudProvider,
      listProjectCloudProviderResources,
      importProjectCloudProviderResource,
      joinProject,
      setAsDefaultProject,
      leaveProject,
      getUsage,
    };
  },
  {
    persist: {
      key: 'gportal-cloud-project',
      pick: ['current'],
    },
  },
);
